import React, { useRef, useEffect, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, RotateCcw, Square, SkipForward } from 'lucide-react';
import { videosApi } from '../services/api';

interface VideoPlayerProps {
  lessonId: string;
  title: string;
  videoUrl?: string; // Para videos locales con URL directa
  onProgress?: (progress: { watchTime: number; completed?: boolean }) => void;
  onEnded?: () => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  lessonId,
  title,
  videoUrl: directVideoUrl,
  onProgress,
  onEnded
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  // const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    loadVideo();
  }, [lessonId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      
      // Reportar progreso cada 10 segundos
      if (Math.floor(video.currentTime) % 10 === 0) {
        onProgress?.({
          watchTime: Math.floor(video.currentTime)
        });
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setLoading(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onProgress?.({
        watchTime: Math.floor(video.currentTime),
        completed: true
      });
      onEnded?.();
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [videoUrl, onProgress, onEnded]);

  const loadVideo = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Si ya tenemos URL directa, usarla (para videos locales y YouTube)
      if (directVideoUrl) {
        const fullUrl = directVideoUrl.startsWith('http') 
          ? directVideoUrl 
          : `https://api.escuela.agentesia.cloud${directVideoUrl}`;
        console.log('🎥 VideoPlayer: directVideoUrl:', directVideoUrl);
        console.log('🎥 VideoPlayer: fullUrl:', fullUrl);
        setVideoUrl(fullUrl);
        setLoading(false);
        return;
      }
      
      // Si no, obtener URL dinámica de la API
      const response = await videosApi.getVideoUrl(lessonId);
      const { videoUrl: signedUrl } = response.data.data;
      setVideoUrl(`https://api.escuela.agentesia.cloud${signedUrl}`);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error cargando el video');
    } finally {
      setLoading(false);
    }
  };

  const togglePlayPause = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (isPlaying) {
        video.pause();
      } else {
        await video.play();
      }
    } catch (error) {
      console.warn('Video play/pause error:', error);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    const progressBar = progressRef.current;
    if (!video || !progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = clickX / width;
    const newTime = percentage * duration;
    
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      video.volume = volume;
      setIsMuted(false);
    } else {
      video.volume = 0;
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!document.fullscreenElement) {
      video.requestFullscreen();
      // setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      // setIsFullscreen(false);
    }
  };

  const stopVideo = () => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    video.currentTime = 0;
    setCurrentTime(0);
    setIsPlaying(false);
  };

  const skipForward = () => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = Math.min(video.currentTime + 10, duration);
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Función para detectar si es un video de YouTube
  const isYouTubeUrl = (url: string) => {
    return /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/i.test(url);
  };

  // Función para extraer el ID del video de YouTube
  const extractYouTubeId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  const isYouTube = isYouTubeUrl(videoUrl);
  const youTubeId = isYouTube ? extractYouTubeId(videoUrl) : null;

  // Render de YouTube
  if (isYouTube && youTubeId) {
    return (
      <div className="relative bg-black rounded-lg overflow-hidden">
        <div className="aspect-video">
          <iframe
            src={`https://www.youtube.com/embed/${youTubeId}?rel=0&modestbranding=1&fs=1&cc_load_policy=0&iv_load_policy=3&autohide=0&controls=1&enablejsapi=1`}
            className="w-full h-full rounded-lg"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={title}
          />
        </div>
        
        <div className="absolute top-4 left-4 right-4 z-10 pointer-events-none">
          <h3 className="text-white font-semibold text-lg drop-shadow-lg bg-black/50 px-3 py-1 rounded-lg">
            {title}
          </h3>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="relative bg-black rounded-lg overflow-hidden aspect-video flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Cargando video...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative bg-black rounded-lg overflow-hidden aspect-video flex items-center justify-center">
        <div className="text-center text-red-400">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <RotateCcw size={32} />
          </div>
          <p className="mb-4">{error}</p>
          <button
            onClick={loadVideo}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-black rounded-lg overflow-hidden group">
      <video
        ref={videoRef}
        className="w-full aspect-video"
        controls
        onClick={togglePlayPause}
        onDoubleClick={toggleFullscreen}
      >
        <source src={videoUrl} type="video/mp4" />
        Tu navegador no soporta la reproducción de video.
      </video>

      {/* Controls Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            onClick={togglePlayPause}
            className="w-20 h-20 bg-blue-600/80 rounded-full flex items-center justify-center text-white hover:bg-blue-600 transition-all duration-200 backdrop-blur-sm"
          >
            {isPlaying ? <Pause size={32} /> : <Play size={32} />}
          </button>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
          {/* Progress Bar */}
          <div
            ref={progressRef}
            className="h-2 bg-white/20 rounded-full cursor-pointer hover:h-3 transition-all duration-200"
            onClick={handleSeek}
          >
            <div
              className="h-full bg-blue-500 rounded-full"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-3">
              <button
                onClick={togglePlayPause}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </button>

              <button
                onClick={stopVideo}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Parar video"
              >
                <Square size={20} />
              </button>

              <button
                onClick={skipForward}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Avanzar 10s"
              >
                <SkipForward size={20} />
              </button>

              <button
                onClick={toggleMute}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>

              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 bg-white/20 rounded-full slider"
                />
              </div>

              <span className="text-sm">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Maximize size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Title Overlay */}
      <div className="absolute top-4 left-4 right-4">
        <h3 className="text-white font-semibold text-lg drop-shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {title}
        </h3>
      </div>
    </div>
  );
};

export default VideoPlayer;